import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Invoice, InvoiceLineItemType } from 'shared/types/graphql';
import { GET_SINGLE_INVOICE } from '../graphql/queries';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';

interface IUseGetBenefitLineItemDiscountInput {
  invoiceIds: string[] | null;
  clinicPetParentId: string;
  shouldCheckForBenefits?: boolean;
}

interface IUseGetBenefitLineItemDiscountOutput {
  invoiceDataLoading: boolean;
  benefitLineItemDiscounts: number;
  refreshBenefitDiscounts: () => void;
}

const useGetBenefitLineItemDiscount = ({
  invoiceIds,
  clinicPetParentId,
  shouldCheckForBenefits,
}: IUseGetBenefitLineItemDiscountInput): IUseGetBenefitLineItemDiscountOutput => {
  const { isFeatureEnabled } = useFeatureFlag();
  const skipQuery = useMemo(() => {
    if (shouldCheckForBenefits && invoiceIds && invoiceIds.length > 0 && isFeatureEnabled(FeatureFlagName.CarePlans)) {
      return false;
    }
    return true;
  }, [shouldCheckForBenefits, invoiceIds, isFeatureEnabled]);

  const {
    data: invoiceLineItemsData,
    loading: loadingInvoiceLineItemsData,
    refetch: refreshBenefitDiscounts,
  } = useQuery<{
    getInvoiceById: Invoice;
  }>(GET_SINGLE_INVOICE, {
    variables: {
      where: { id: invoiceIds?.[0] || '' },
      clinicPetParentId,
    },

    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    skip: skipQuery,
  });

  const invoice = invoiceLineItemsData?.getInvoiceById || null;
  const filteredInvoiceLineItems = useMemo(() => {
    if (invoice && invoice.lineItems) {
      return invoice.lineItems
        .filter((lineItem) => lineItem.type === InvoiceLineItemType.Code)
        .filter((lineItem) => lineItem.amount + lineItem.fee - lineItem.discount >= 0);
    }
    return [];
  }, [invoice]);

  const benefitLineItemDiscounts = useMemo(() => {
    let total = 0;
    if (filteredInvoiceLineItems.length === 0) return total;
    filteredInvoiceLineItems.forEach((item) => {
      item.connectedCareBenefits?.forEach((benefit) => {
        benefit && (total += benefit.adjustedAmount);
      });
    });
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInvoiceLineItems, invoiceIds]);

  return {
    benefitLineItemDiscounts: benefitLineItemDiscounts,
    invoiceDataLoading: loadingInvoiceLineItemsData,
    refreshBenefitDiscounts,
  };
};

export default useGetBenefitLineItemDiscount;
